import type { ChangeEvent, FC } from 'react'
import { InputAdornment, OutlinedInput, IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Search, Close, Empty, ScanBarcode } from '@dhl/icons'

type Props = {
  onChange: (term: string) => void
  searchTerm: string
  placeholder?: string
  showClearButton?: boolean
  /**
   * Visually show consumer its also possible to use the scanner to search when there is no current searchTerm
   * @default: false
   */
  showScanIcon?: boolean
  autoFocus?: boolean
  fullWidth?: boolean
  fullHeight?: boolean
  size?: 'small' | 'medium'
}

export const SearchTextField: FC<Props> = ({
  onChange,
  searchTerm,
  placeholder,
  showClearButton = false,
  showScanIcon = false,
  fullWidth = true,
  fullHeight = false,
  autoFocus,
  size = 'small',
}) => {
  const { t } = useTranslation()

  // eslint-disable-next-line consistent-return
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.indexOf('%') >= 0) {
      return false
    }
    onChange(e.target.value)
  }

  return (
    <OutlinedInput
      fullWidth={fullWidth}
      size={size}
      data-test="search-input"
      value={searchTerm}
      onChange={handleChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      sx={{
        '& .MuiInputBase-input': {
          ...(size === 'medium' && {
            px: 0.5,
            py: 1.4,
          }),
        },
        ...(fullHeight && {
          height: '100%',
        }),
      }}
      startAdornment={
        <InputAdornment position="start">
          <Search color="action" />
        </InputAdornment>
      }
      endAdornment={
        <>
          {showClearButton && (
            <InputAdornment position="end">
              <IconButton
                disabled={!searchTerm}
                onClick={() => onChange('')}
                edge="end"
                aria-label={t('components.seachTextField.clearSearchButtonAriaLabel')}
                data-test="clear-search-button"
                size="large"
              >
                {searchTerm ? <Close color="action" /> : <Empty />}
              </IconButton>
            </InputAdornment>
          )}

          {showScanIcon && (
            <InputAdornment position="end">
              <ScanBarcode color="action" />
            </InputAdornment>
          )}
        </>
      }
    />
  )
}
